import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getListings, ListingItem } from "@/utils/api";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useSite } from "@/context/SiteContext";

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours === 0) {
      const mins = Math.floor(diff / (1000 * 60));
      return `${mins}分钟前`;
    }
    return `${hours}小时前`;
  }
  if (days === 1) return "昨天";
  if (days < 7) return `${days}天前`;
  if (days < 30) return `${Math.floor(days / 7)}周前`;
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}

function ListingCard({ listing, index }: { listing: ListingItem; index: number }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  const toggleFavorite = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <Link
        to={`/listing/${listing.id}`}
        className="group flex gap-3 md:gap-4 p-3 md:p-4 bg-white rounded-xl border border-gray-100 hover:border-orange-200 transition-all duration-300 hover:shadow-sm"
      >
        {/* Image */}
        <div className="w-24 h-24 md:w-32 md:h-32 shrink-0 rounded-lg overflow-hidden bg-gray-100">
          {listing.images && listing.images[0] ? (
            <img
              src={listing.images[0]}
              alt={listing.title}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <i className="ri-image-line text-2xl" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-sm md:text-base font-semibold text-gray-800 group-hover:text-orange-600 transition-colors line-clamp-1">
                {listing.is_pinned && (
                  <span className="inline-flex items-center px-1.5 py-0.5 bg-orange-100 text-orange-600 text-xs rounded mr-1.5 shrink-0">
                    置顶
                  </span>
                )}
                {listing.title}
              </h3>
            </div>

            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-xs rounded-full">
                {listing.category?.name || '未分类'}
              </span>
              <span className="text-gray-400 text-xs">
                {formatDate(listing.created_at)}
              </span>
              <span className="text-gray-400 text-xs flex items-center gap-0.5">
                <i className="ri-eye-line" />
                {listing.view_count}
              </span>
            </div>

            <p className="text-gray-500 text-xs md:text-sm mt-2 line-clamp-2 leading-relaxed">
              {listing.content}
            </p>
          </div>

          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <i className="ri-user-line text-xs text-gray-400" />
              <span className="text-xs text-gray-500">
                {listing.contact_name || '匿名用户'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={(e) => toggleFavorite(listing.id, e)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <i
                  className={`${
                    favorites.has(listing.id)
                      ? "ri-heart-fill text-orange-500"
                      : "ri-heart-line text-gray-400"
                  } text-base`}
                />
              </button>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default function LatestListings() {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.3 });
  const { siteId, loading: siteLoading } = useSite();
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 如果站点还在加载或没有 siteId，不发送请求
    if (siteLoading || !siteId) {
      return;
    }

    const loadListings = async () => {
      try {
        const data = await getListings({ page_size: 6 });
        setListings(data.list || []);
      } catch (err) {
        console.error('加载最新信息失败:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadListings();
  }, [siteId, siteLoading]);

  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div 
            ref={titleRef}
            className={`flex items-center justify-between mb-6 md:mb-8 transition-all duration-600 ${
              titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
                最新发布
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                发现最新上线的商业服务信息
              </p>
            </div>
            <Link
              to="/category/enterprise-services"
              className="text-orange-600 text-sm font-medium hover:text-orange-700 transition-colors flex items-center gap-1 whitespace-nowrap"
            >
              查看更多
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {/* Listings Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full"></div>
              <span className="ml-3 text-gray-500">加载中...</span>
            </div>
          ) : listings.length > 0 ? (
            <div className="space-y-3 md:space-y-4">
              {listings.map((listing, index) => (
                <ListingCard key={listing.id} listing={listing} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-gray-100">
                <i className="ri-inbox-line text-2xl text-gray-400" />
              </div>
              <p className="text-gray-500">暂无信息</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
